import {
  BlocksCategory,
  FindBlocksCategoriesResponse,
  LibraryService,
} from 'base/library_service';
import { Locale } from 'base/ui/locales';
import { normalizeSectionName } from 'base/webflow/page_component';

export type LoadApprovedCategories = () => Promise<ApprovedCategories>;

export class ApprovedCategories {
  static createLoader(library: LibraryService): LoadApprovedCategories {
    let load: ReturnType<LoadApprovedCategories> | undefined;
    return () => {
      if (load == null) {
        load = library
          .findBlocksCategories()
          .then((r) => new ApprovedCategories(r));
      }
      return load;
    };
  }

  private constructor(private readonly res: FindBlocksCategoriesResponse) {}

  readonly all = new BlocksCategoriesList(this.res.blocksCategories);
  readonly forGeneratePage = new BlocksCategoriesList(
    this.res.blocksCategories.filter((c) => c.includeInGeneratePage),
  );

  readonly homeTranslations = this.res.homeTranslations;
}

class BlocksCategoriesList {
  constructor(private readonly categories: readonly BlocksCategory[]) {}

  /** A CSV-like format for use in LLM prompts */
  createCsv(locale: Locale) {
    return [
      ['id', 'section category'].join('|'),
      ...this.categories.map((c, i) =>
        [`${i + 1}`, c.translations.get(locale) || c.name].join('|'),
      ),
    ].join('\n');
  }

  readonly englishNames = this.categories.map((c) => c.name);

  readonly normalizedToIndex = new Map<string, number>(
    this.categories.flatMap((c, i): [string, number][] => [
      [normalizeSectionName(c.name), i],
      ...[...c.translations.values()].map((t): [string, number] => [
        normalizeSectionName(t),
        i,
      ]),
    ]),
  );

  /** Finds the closest approved category for a section name */
  findClosest(sectionName: string) {
    sectionName = normalizeSectionName(sectionName);
    const index0 = this.normalizedToIndex.get(sectionName);
    if (index0 == null) return;
    return { ...this.categories[index0], index0, index1: index0 + 1 };
  }
}
